/* eslint-disable @typescript-eslint/no-this-alias */
import * as Sentry from '@sentry/browser';

import {
  DEBUG_MODE,
  getBuildVersionToShow,
  getUrlChangeSex,
  getUrlSurveyChangeDates,
  getUrlSurveyCriticalPoint,
  getUrlSurveySupervisors,
  getUrlTestChangeDates,
  isDemoMode,
  runLocalTest,
  setRunTestWithCore,
  ulrDefaultDbTexts,
  useMonitor,
} from '../spdConfig';
import { SPD } from '../spd';

import * as Configuration from './testConfiguration';
import { TypeActionOnActivePicture } from './activePicture';
import { Announcement } from './announcement';
import { FooterTools } from './footerTools';
import { LoaderError, Loader } from './loader';
import { Spin } from './spin';
import {
  Survey,
  ModeSurvey,
  DataSurveyTemplate,
  InfoAfterExam,
  Supervisor,
} from './survey';
import { Test } from './test'; // lub odpowiednia ścieżka
import { ActiveStep } from './activeStep';
import { TimersManager } from './timersManager';
import { Speaker } from './speaker';
import { SupervisorMonitor } from './supervisorMonitor';
import { FullScreen } from './fullScreen';
import { TextId, TextSelector } from './textSelector';
import { AuthError, AuthStatus, publicSignIn } from './auth';

declare let spd: SPD;
declare let testConfiguration: Configuration.TestConfiguration;
declare let speaker: Speaker;
declare let supervisorMonitor: SupervisorMonitor;
declare let defaultDbTextSelector: TextSelector;

export class SurveyManager {
  private activeTest: Test | null = null;
  protected indexTest = 0;
  protected survey: Survey | null = null;
  protected timers: TimersManager;
  private monitorCameraIssueActive = false;

  private surveyPIN = '';
  private surveyExternalId = '';

  constructor() {
    this.timers = new TimersManager();
  }

  public clearAllTimers() {
    this.timers.clearAllTimers();
  }

  public doNextTask(taskNumber: string, code: string | null) {
    this.recordActivity();
    this.activeTest && this.activeTest.doNextTask(taskNumber, code);
  }

  public doPrevTask(taskNumber: string, code: string) {
    this.recordActivity();
    this.activeTest && this.activeTest.doPrevTask(taskNumber, code);
  }

  public doAction(taskNumber: string, code: string, param?: string): boolean {
    if (this.activeTest) {
      this.recordActivity();
      DEBUG_MODE && console.log('spd.doAction:', taskNumber, code, param);
      return this.activeTest.doAction(taskNumber, code, param);
    } else {
      return false;
    }
  }

  public showTaskHint(nr: number) {
    this.activeTest && this.activeTest.showTaskHint(nr);
  }

  public clearTaskHint() {
    this.activeTest && this.activeTest.clearTaskHint();
  }

  public playClick() {
    this.activeTest?.playClick();
  }

  public playGoodClick() {
    this.activeTest?.playGoodClick();
  }

  public playBadClick() {
    this.activeTest?.playBadClick();
  }

  public setSkipNextClickSound() {
    if (this.activeTest) {
      this.activeTest.skipNextClickSound = true;
    }
  }

  public getIndicatorResultsPayload() {
    return this.activeTest?.buildIndicatorResultsPayload() || null;
  }

  public getIndicatorTestFileName() {
    return this.activeTest?.getTestFileName() || null;
  }

  public getTemplateParameters() {
    if (this.activeTest) {
      return this.activeTest.getTemplateParameters();
    }
  }

  public clearPicture(taskNumber: string, code: string, param?: string) {
    this.recordActivity();
    this.activeTest &&
      this.activeTest.doActionOnActivePicture(
        TypeActionOnActivePicture.Clear,
        taskNumber,
        code,
        param,
      );
  }

  public takePhoto(taskNumber: string, code: string, param?: string) {
    this.recordActivity();
    this.activeTest &&
      this.activeTest.doActionOnActivePicture(
        TypeActionOnActivePicture.TakePhoto,
        taskNumber,
        code,
        param,
      );
  }

  public switchCamera(taskNumber: string, code: string, param?: string) {
    this.recordActivity();
    this.activeTest &&
      this.activeTest.doActionOnActivePicture(
        TypeActionOnActivePicture.SwitchCamera,
        taskNumber,
        code,
        param,
      );
  }

  protected async finishActiveTest() {
    if (this.activeTest) {
      const testSign = this.activeTest.testSign;
      Spin.showSpin('saving', '...');
      const timeout = window.setTimeout(() => {
        Spin.showSpin('saving', '...', {
          subtitleTextId: 'saving_with_connection_problem',
        });
      }, 15000);
      const timeout2 = window.setTimeout(() => {
        const detailsHtml1 = `Id: ${testSign.id}, Name: ${testSign.name} <br/>`;
        const detailsHtml2 = Loader.ErrorsStack.toReadableString().replace(
          /\n/g,
          '<br/>',
        );
        Spin.showSpin('saving', '', {
          subtitleTextId: 'saving_connection_problem_details',
          detailsTitleTextId: 'saving_details_title',
          detailsHtml: detailsHtml1 + detailsHtml2,
          showRestartButton: true,
          restartButtonTextId: 'restart_page',
        });
      }, 60000);
      try {
        await this.activeTest.waitForAllSaveResults(); // czekam do skutku na zapis wszystkich wyników
        await this.sendTestStatus(this.activeTest, false); // jest aktywny test więc przed uruchomieniem nowego informuję o jego zakończeniu
      } catch (error) {
        // błędy zapisu obsłużone w Results, tu logujemy nieoczekiwane wyjątki
        DEBUG_MODE && console.warn((error as Error).message);
        Sentry.captureException(error);
        this.activeTest.useblockingErrorSeries(); /// oznaczam komunikat blokujący by z tego wykonania testu już nie informować o błędach
        Announcement.announcementAboutBlockingError(
          'save_all_results_problem',
          true,
        );
      } finally {
        Loader.ErrorsStack.clear();
        window.clearTimeout(timeout);
        window.clearTimeout(timeout2);
      }
    }
  }

  protected async finishActiveTestAndRunNext(nextIndexTest?: number) {
    await this.finishActiveTest(); /// zatrzymam tylko aktywny test
    this.indexTest = nextIndexTest || ++this.indexTest;
    await this.runTest(this.indexTest); /// uruchamian następny test lub kończę badanie
  }

  protected async finishActiveTestAndRunTestByIdent(identTest: string) {
    await this.finishActiveTest(); /// zatrzymam tylko aktywny test
    if (this.survey) {
      this.indexTest = this.survey.getIndexTestbyIdent(identTest); //znajduję index testu o podanym identyfikatorze
    }
    await this.runTest(this.indexTest); /// uruchamian jako następny test lub kończę badanie
  }

  /// przerwanie wykonywania całego badania
  public async doEndSurvey() {
    await this.finishActiveTest();
    await this.endOfSurvey(true);
  }

  protected async isSurveyExtended() {
    if (!this.survey) {
      return false;
    }

    let updatedSurvey = false;
    try {
      //pobieram aktualny stan badania na podstawie obowiązujących
      updatedSurvey = this.surveyExternalId
        ? await this.survey.authorizePublicUser(
            this.surveyPIN,
            this.surveyExternalId,
          )
        : await this.survey.authorize(this.surveyPIN);
    } catch (e) {
      await Announcement.announcementLoaderErrorForPINAsync(e as LoaderError);
      return false;
    }

    if (!updatedSurvey) {
      return false;
    }
    if (useMonitor && this.survey.getModeSurvey() === ModeSurvey.Examined) {
      supervisorMonitor.setEstimatedDurationSeconds(
        this.calculateEstimatedDurationSeconds(),
      );
      supervisorMonitor.sendSurveyInformation();
    }
    this.indexTest = this.survey.getIndexStartTest();
    DEBUG_MODE && console.log('isSurveyExtended', this.indexTest);

    if (this.indexTest < 0) {
      return false; /// nie ma kolejnych testów
    }

    await this.runTest(this.indexTest); // doszły testy do wykonania
    this.activeTest &&
      ActiveStep.setActiveStepAfter(() => this.activeTest?.reloadTest());

    return true;
  }

  protected async getSurveyCriticalPoint(
    surveyId: string,
  ): Promise<InfoAfterExam> {
    try {
      const json = await Loader.getSyncJSONWithVersion(
        getUrlSurveyCriticalPoint(surveyId),
        null,
        !runLocalTest(),
      );
      DEBUG_MODE && console.log('infoForExamined', json);
      return {
        infoForExamined: json.infoForExamined || [],
        helpExams: json.helpExams || [],
        specialisedConsultations: json.specialisedConsultations || [],
      };
    } catch (e) {
      console.error(e);
      return {
        infoForExamined: [],
        helpExams: [],
        specialisedConsultations: [],
      };
    }
  }

  protected async endOfSurvey(forceEnd = false) {
    const that = this;
    const stopMonitorStreaming = () => {
      if (useMonitor) {
        supervisorMonitor.stopStreaming();
      }
    };

    if (isDemoMode()) {
      stopMonitorStreaming();
      return spd.restartSPD();
    }

    if (that.survey) {
      const psychologist = that.survey.getModeSurvey() !== ModeSurvey.Examined;

      if (!forceEnd && !psychologist && (await that.isSurveyExtended())) {
        return; /// badanie zostało rozszerzone więc go nie będę kończył, wykonanie wróci tu, gdy wszystkie testy dodatkowe zostaną zrobione
      }

      const surveyId = that.survey?.getId();

      let info = null;
      that.clearAllTimers();
      stopMonitorStreaming();

      if (surveyId) {
        await that.sendSurveyStatus(surveyId, false);
        if (!psychologist) {
          info = await that.getSurveyCriticalPoint(surveyId);
        }
      }

      Loader.loadContentWithVersion(
        '#main',
        'template/survey_page-template.mst',
        that.survey.getTextsForEndPage(
          psychologist,
          this.isExternalSurvey(),
          info,
        ),
        function (surveyText: DataSurveyTemplate) {
          Test.clearTestThemeCSS(); ///usuwam CSS-y testu jeśli były dynamicznie dodawane
          FooterTools.addTools();
          surveyText.text_description?.text_read &&
            void speaker.playSoundOrSpeakText(
              surveyText.text_description.text_read,
              false,
              surveyText.text_description.sound,
            );
          if (!psychologist) {
            that.timers.setTimer(
              'timeGoToStart',
              that.survey?.getTimeGoToStart(),
              () => {
                if (that.isExternalSurvey()) {
                  spd.loadFinishPage();
                  return;
                }
                spd.restartSPD();
              },
            );
          }
        },
      );
      ActiveStep.setActiveStepAfter(() => that.endOfSurvey());
      return;
    }
    stopMonitorStreaming();
  }

  protected async sendTestStatus(test: Test, start: boolean) {
    if (
      runLocalTest() ||
      this.survey?.getModeSurvey() !== ModeSurvey.Examined
    ) {
      return;
    }
    const data = start
      ? {
          startDate: new Date().toISOString(),
          startVersion: getBuildVersionToShow(),
        }
      : {
          endDate: new Date().toISOString(),
          endVersion: getBuildVersionToShow(),
        };
    const onError = start
      ? (error: unknown) => {
          const loaderError = error as LoaderError;
          if (!Announcement.announcementBlockadeActive) {
            Announcement.announcementBlockade(
              'error_patch',
              true,
              loaderError?.message,
            );
          }
        }
      : (error: unknown) => {
          const loaderError = error as LoaderError;
          Loader.ErrorsStack.add(loaderError); /// nie wyświetlam komunikatu - wszystko zostanie obsłużone przy zapisie końcowym
        };
    await Loader.patchWithRetry(
      getUrlTestChangeDates(test.surveyId, test.testSign.id),
      data,
      onError,
    );
    Announcement.closeBlockade();
  }

  protected async sendSurveyStatus(surveyId: string, start: boolean) {
    if (
      runLocalTest() ||
      this.survey?.getModeSurvey() !== ModeSurvey.Examined
    ) {
      return;
    }
    const data = start
      ? {
          startDate: new Date().toISOString(),
          startVersion: getBuildVersionToShow(),
        }
      : {
          endDate: new Date().toISOString(),
          endVersion: getBuildVersionToShow(),
        };
    const onError = (error: unknown) => {
      const loaderError = error as LoaderError;
      if (!Announcement.announcementBlockadeActive) {
        Announcement.announcementBlockade(
          'error_patch',
          true,
          loaderError?.message,
        );
      }
    };
    await Loader.patchWithRetry(
      getUrlSurveyChangeDates(surveyId),
      data,
      onError,
    );
    Announcement.closeBlockade();
  }

  protected async runTest(indexTest: number, startTask?: number) {
    if (this.survey) {
      const surveyId = this.survey.getId();
      const signTest = this.survey.getTestSignatureByIndex(indexTest);
      if (surveyId && signTest) {
        Spin.showSpin('loading', '...');
        this.activeTest = await Test.loadTest(
          this.survey.getModeSurvey(),
          () => this.finishActiveTestAndRunNext(),
          surveyId,
          signTest,
          startTask,
        );
        if (this.activeTest) {
          await this.sendTestStatus(this.activeTest, true);
          return;
        }
      }
    }
    await this.endOfSurvey();
  }

  public async doTest(taskNumber: string, code: string, param?: number) {
    this.recordActivity();
    this.activeTest?.playClick();
    this.activeTest?.stopTest();
    await this.finishActiveTestAndRunNext(param);
  }

  public async doTestIdent(taskNumber: string, code: string, param?: string) {
    this.recordActivity();
    this.activeTest?.playClick();
    this.activeTest?.stopTest();
    await this.finishActiveTestAndRunTestByIdent(param || '');
  }

  protected async verifyCamera() {
    try {
      Announcement.announcementBlockade('checking_camera');
      await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      Announcement.closeBlockade();
    } catch (error) {
      Announcement.closeBlockade();
      await Announcement.announcementAlert('required_camera');
      spd.restartSPD();
    }

    if (!FullScreen.isOpenFullscreen()) {
      await Announcement.announcementAlert('run_fullscreen');
      await FullScreen.openFullscreen();
    }
  }

  public async startSurvey() {
    const surveyId = this.survey?.getId();
    if (this.survey && surveyId) {
      const surveyNumber = this.survey.getNumber();
      const cameraRequired = this.survey.getCameraRequired();
      const psychologist = this.survey.getModeSurvey() !== ModeSurvey.Examined;
      if (useMonitor && !psychologist && cameraRequired) {
        supervisorMonitor.setEstimatedDurationSeconds(
          this.calculateEstimatedDurationSeconds(),
        );
        const supervisors = await this.getSurveySupervisors(surveyId);
        supervisorMonitor.setSupervisors(
          supervisors.map(supervisor => ({
            name: supervisor.name,
            role: supervisor.role,
          })),
        );
        void supervisorMonitor.activateCommunication(
          surveyId,
          surveyNumber,
          true,
        ); // nie czekam aż się uruchomi nadzór
        if (cameraRequired) {
          await this.verifyCamera(); /// czekam na zweryfikowanie kamery
        }
      }

      this.indexTest = psychologist ? 0 : this.survey.getIndexStartTest();
      await this.sendSurveyStatus(surveyId, true);

      await this.runTest(this.indexTest);
      this.activeTest &&
        ActiveStep.setActiveStepAfter(() => this.activeTest?.reloadTest());
    }
  }
  //pozwala uruchomić platformę z konkretnym zadaniem - do szybkiego testowania
  public async loadTestTaskPage(
    startTest: number,
    startTask: number,
    id: string,
    core: boolean,
  ) {
    setRunTestWithCore(core);
    const that = this;
    that.survey = new Survey();
    try {
      if (await that.survey.authorizeById(id)) {
        testConfiguration.lange = that.survey.getLange();
        testConfiguration.gender = that.survey.getGender();
        supervisorMonitor.setLange(testConfiguration.lange);
        that.indexTest = startTest;
        await that.runTest(startTest, startTask);
        that.activeTest &&
          ActiveStep.setActiveStepAfter(() => that.activeTest?.reloadTest());
      }
    } catch (e) {
      await Announcement.announcementLoaderErrorForPINAsync(e as LoaderError);
    }
  }

  protected async getSurveySupervisors(
    surveyId: string,
  ): Promise<Supervisor[]> {
    try {
      const json = await Loader.getSyncJSONWithVersion(
        getUrlSurveySupervisors(surveyId),
        null,
        !runLocalTest(),
      );
      DEBUG_MODE && console.log('getSurveySupervisors', json);
      return json.supervisors;
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  protected async loadSurveyPage() {
    const that = this;

    if (isDemoMode()) {
      return spd.startSurvey();
    }

    const surveyId = that.survey?.getId();
    let supervisors: Supervisor[] = [];

    if (surveyId) {
      supervisors = await that.getSurveySupervisors(surveyId);
    }

    Loader.loadContentWithVersion(
      '#main',
      'template/survey_page-template.mst',
      that.survey?.getTextsForStartPage(supervisors),
      async function (surveyText: DataSurveyTemplate) {
        FooterTools.addTools();
        surveyText.text_description?.text_read &&
          (await speaker.playSoundOrSpeakText(
            surveyText.text_description.text_read,
            false,
            surveyText.text_description.sound,
          ));
        surveyText.text_announcements_prefix?.text_read &&
          (await speaker.playSoundOrSpeakText(
            surveyText.text_announcements_prefix.text_read,
            false,
            surveyText.text_announcements_prefix.sound,
          ));
        surveyText.text_announcements_postfix?.text_read &&
          (await speaker.playSoundOrSpeakText(
            surveyText.text_announcements_postfix.text_read,
            false,
            surveyText.text_announcements_postfix.sound,
          ));
      },
    );
    ActiveStep.setActiveStepAfter(() => that.loadSurveyPage());
  }

  public async publicSignIn(callback: () => Promise<void>) {
    try {
      const signedInStatus = await publicSignIn();
      switch (signedInStatus) {
        case AuthStatus.CorrectAuth:
          await callback?.();
          break;
        case AuthStatus.WrongRole:
          Announcement.announcementAbout('wrong_public_user_role');
          break;
      }
    } catch (error) {
      Announcement.announcementAuthError(error as AuthError);
    }
  }

  private async authorizeSurveyForPublicUserGeneric(
    authorizeMethod: (survey: Survey) => Promise<boolean>,
    callbackPage: () => void,
    callbackPageFailed: () => void,
    errorHandler?: (e: unknown) => void | Promise<void>,
  ) {
    const that = this;
    await that.publicSignIn(async () => {
      // weryfikacja autoryzacji - jeśli ok to dalej jeśli nie informacja zwrotna i powrót
      that.survey = new Survey();
      try {
        if (await authorizeMethod(that.survey)) {
          if (that.survey.isSetGender()) {
            await that.setGender(that.survey.getGender());
          } else {
            callbackPage?.();
          }
        }
      } catch (e) {
        await errorHandler?.(e);
        callbackPageFailed?.();
      }
    });
  }

  public async authorizeSurveyForPublicUser(
    pin: string,
    externalId: string,
    callbackPage: () => void,
    callbackPageFailed: () => void,
  ) {
    this.surveyPIN = pin;
    this.surveyExternalId = externalId;
    await this.authorizeSurveyForPublicUserGeneric(
      survey => survey.authorizePublicUser(pin, externalId),
      callbackPage,
      callbackPageFailed,
      async e => {
        await Announcement.announcementLoaderErrorForPINAsync(e as LoaderError);
      },
    );
  }

  public async authorizeSurveyByToken(
    token: string,
    callbackPage: () => void,
    callbackPageFailed: () => void,
  ) {
    await this.authorizeSurveyForPublicUserGeneric(
      survey => survey.authorizeByToken(token),
      callbackPage,
      callbackPageFailed,
    );
    this.surveyPIN = this.survey?.getPin() || '';
    this.surveyExternalId = this.survey?.getId() || '';
  }

  public async authorizeSurveyByPin(
    pin: string,
    callbackPage: () => void,
    callbackPageFailed: () => void,
  ) {
    const that = this;
    that.surveyPIN = pin;

    // weryfikacja autoryzacji - jeśli ok to dalej jeśli nie informacja zwrotna i powrót
    that.survey = new Survey();

    try {
      if (await that.survey.authorize(that.surveyPIN)) {
        if (that.survey.isSetGender()) {
          await that.setGender(that.survey.getGender());
        } else {
          callbackPage?.();
        }
      }
    } catch (e) {
      await Announcement.announcementLoaderErrorForPINAsync(e as LoaderError);
      callbackPageFailed?.();
    }
  }

  public onInput() {
    this.activeTest?.showConditionallyForInputChange();
  }

  public reloadTask() {
    this.activeTest?.reloadTest();
  }

  public setReactionStartTime() {
    this.activeTest?.setReactionStartTime();
  }

  public setLatencyStartTime(latencyTime: number) {
    this.activeTest?.setLatencyTime(latencyTime);
  }

  public setDecisionTime() {
    this.activeTest?.setDecisionTime();
  }

  public verifyOnline() {
    const that = this;
    const requiresMonitorCamera = () => {
      return (
        useMonitor &&
        that.survey?.getModeSurvey() === ModeSurvey.Examined &&
        !!that.survey?.getCameraRequired()
      );
    };
    async function handleOffline() {
      that.activeTest?.pauseTask(true);
      Announcement.announcementBlockade('handle_offline', true);
    }

    function handleOnline() {
      const restarted = that.activeTest?.pauseTask(false);
      if (!restarted) {
        that.activeTest?.reloadTask();
      }
      Announcement.closeBlockade();
      DEBUG_MODE && console.log('Połączenie z Internetem zostało przywrócone.');
    }

    function handleBlockingError() {
      DEBUG_MODE && console.log('handleBlockingError', that.activeTest);
      that.activeTest?.pauseTask(false);
      that.activeTest?.reloadTest(true);
    }

    function handleMonitorCameraDisconnected() {
      if (!requiresMonitorCamera() || !that.activeTest) {
        return;
      }
      that.activeTest.pauseTask(true);
      that.monitorCameraIssueActive = true;
      Announcement.announcementBlockade('required_camera_during_exam', true);
    }

    function handleMonitorCameraRecovered() {
      if (!that.monitorCameraIssueActive) {
        return;
      }
      that.monitorCameraIssueActive = false;
      const restarted = that.activeTest?.pauseTask(false);
      if (!restarted) {
        that.activeTest?.reloadTask();
      }
      if (navigator.onLine) {
        Announcement.closeBlockade();
      }
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('blockingError', handleBlockingError);
    window.addEventListener(
      'monitor-camera-disconnected',
      handleMonitorCameraDisconnected,
    );
    window.addEventListener(
      'monitor-camera-recovered',
      handleMonitorCameraRecovered,
    );
  }

  public async playSoundOrSpeakText(textId: TextId, must_read = true) {
    await this.activeTest?.playSoundOrSpeakText(textId, must_read);
  }

  public getTextPresentation(textId: TextId) {
    return this.activeTest?.getTextPresentation(textId);
  }

  public setOriginalValue(value: string) {
    this.activeTest?.setOriginalValue(value);
  }

  public getValue() {
    return this.activeTest?.getValue();
  }

  public getValueFromTask(taskNumber: string) {
    return this.activeTest?.getValueFromTask(taskNumber);
  }

  public async setGender(gender: string) {
    const that = this;
    testConfiguration.gender = (gender ||
      testConfiguration.gender) as Configuration.Gender;
    const surveyId = that.survey?.getId();
    if (surveyId && !runLocalTest()) {
      const onError = (error: unknown) => {
        const loaderError = error as LoaderError;
        if (!Announcement.announcementBlockadeActive) {
          Announcement.announcementBlockade(
            'error_patch',
            true,
            loaderError?.message,
          );
        }
      };
      await Loader.patchWithRetry(
        getUrlChangeSex(surveyId),
        {
          sex: testConfiguration.gender,
        },
        onError,
      );
      Announcement.closeBlockade();
    }
    await that.loadSurveyPage();
  }

  private calculateEstimatedDurationSeconds() {
    const defaultMs = 120;
    const tests = this.survey?.getTests() || [];
    const totalSec = tests.reduce((sum, test) => {
      const duration =
        typeof test.average_duration === 'number' && test.average_duration !== 0
          ? test.average_duration
          : defaultMs;
      return sum + duration;
    }, 0);
    return totalSec;
  }

  private recordActivity() {
    if (!useMonitor || this.survey?.getModeSurvey() !== ModeSurvey.Examined) {
      return;
    }
    supervisorMonitor.markActivity();
    supervisorMonitor.sendSurveyInformation();
  }

  public async runPsychologistMode(
    id: string,
    mode: ModeSurvey,
    core: boolean,
  ) {
    const that = this;
    setRunTestWithCore(core);
    that.clearAllTimers();
    await defaultDbTextSelector.loadDbTexts(ulrDefaultDbTexts);

    that.survey = new Survey(mode);
    try {
      if (await that.survey.authorizeById(id)) {
        const gender = that.survey.isSetGender()
          ? that.survey.getGender()
          : Configuration.Gender.Male;
        testConfiguration.gender = (gender ||
          testConfiguration.gender) as Configuration.Gender;

        return that.startSurvey();
      }
    } catch (e) {
      await Announcement.announcementLoaderErrorForPINAsync(e as LoaderError);
    }
  }

  public getSurveyNumber() {
    return this.survey?.getNumber() || null;
  }

  public getDateOfBirth() {
    return this.survey?.getBirthday() || null;
  }

  public isExternalSurvey() {
    return this.surveyExternalId !== '';
  }
}
